import { extractS3Key } from '@trilirivos/media-keys';
import { FieldArray } from 'formik';
import { List, ListOrdered, ListPlus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { uploadImage } from '../../../api/upload-image';
import ButtonComponent from '../../../components/UI/Button/Button.component';
import { BUTTON_LABELS } from '../../../constants/button-labels.constants';
import {
  ALLOWED_IMAGE_TYPES,
  MAX_FILE_SIZE,
} from '../../../constants/draft.constants';
import type { SetFieldValue } from '../../../constants/textEditor.constants';
import { deleteImageFromS3 } from '../../../helpers/deleteImagefromS3';
import { downscaleImageFile } from '../../../helpers/downscaleImageFile';
import type {
  DraftInterfaceState,
  PostReplyDraftInterfaceState,
} from '../../../interfaces/post.interface';
import type { TextBlock } from '../../../interfaces/textEditor.interface';
import { UPLOAD_ERRORS } from '../../../services/getPresignedUrlREST';
import { draftSlice } from '../../../store/draft.store';
import { InputField } from '../../UI/InputField';
import { handlePaste } from '../handlers/handlePaste';
import { CaseHeading } from './BlockTypes/CaseHeading';
import { CaseParagraph } from './BlockTypes/CaseParagraph';

/**
 * Provides properties used in rendering a block component within a text editor.
 */
interface RenderBlockProps {
  values: any;
  block: TextBlock;
  index: number;
  arrayHelpers: any;
  setFieldValue: SetFieldValue;
  handleOpenHeadingMenu: (
    event: React.MouseEvent<HTMLButtonElement>,
    blockId: string,
  ) => void;
  headingMenuAnchors: { [p: string]: HTMLElement | null };
  handleCloseHeadingMenu: (blockId: string) => void;
  handleHeadingLevelChange: (
    index: number,
    level: number,
    setFieldValue: any,
  ) => void;
  fileInputRef: React.MutableRefObject<null>;
}

/**
 * RenderBlock component is responsible for rendering different types of content blocks such as Paragraph, Heading, List,
 * Image, Code, and Quote. It handles the appropriate rendering and interaction based on the type of content block provided.
 *
 * @param {RenderBlockProps} props - The properties passed to the RenderBlock component,
 * which include values, block, index, arrayHelpers, setFieldValue, handleOpenHeadingMenu, headingMenuAnchors,
 * handleCloseHeadingMenu, handleHeadingLevelChange, and fileInputRef.
 *
 * @returns {React.ReactElement} The rendered React.ReactElement for the given block type.
 */
export const RenderBlock = (props: RenderBlockProps): React.ReactElement => {
  const {
    values,
    block,
    index,
    arrayHelpers,
    setFieldValue,
    handleOpenHeadingMenu,
    headingMenuAnchors,
    handleCloseHeadingMenu,
    handleHeadingLevelChange,
    fileInputRef,
  } = props;

  // Add state for tracking upload status
  const [uploadStatus, setUploadStatus] = React.useState<{
    loading: boolean;
    error: string | null;
  }>({
    loading: false,
    error: null,
  });
  const dispatch = useDispatch();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const { isEditingDraft, isAddingDraft, isAddingPostReplyAsDraft } =
    useSelector((state: DraftInterfaceState) => state.draftStore);
  const { isEditingPostReplyDraft } = useSelector(
    (state: PostReplyDraftInterfaceState) => state.postReplyDraftStore,
  );
  const { SAVED_IMAGE_KEY } = draftSlice.actions;
  const arrayOfValues =
    isEditingDraft || isEditingPostReplyDraft ? values.blocks : values;

  // TRIL-258: post replies use H3 as the only title heading. Coerce any reply
  // heading block whose level isn't 3 so the saved/published reply renders H3.
  // Reply composer = editing a reply draft OR the add-new-reply "Create Draft"
  // modal (isAddingPostReplyAsDraft). The typeof guard keeps this from looping
  // if the level reads undefined under a different values shape. Posts unaffected.
  const isReplyComposer = isEditingPostReplyDraft || isAddingPostReplyAsDraft;
  const currentHeadingLevel = arrayOfValues?.[index]?.data?.level;
  React.useEffect(() => {
    if (
      isReplyComposer &&
      block.type === 'Heading' &&
      typeof currentHeadingLevel === 'number' &&
      currentHeadingLevel !== 3
    ) {
      setFieldValue(`blocks.${index}.data.level`, 3);
    }
  }, [isReplyComposer, block.type, currentHeadingLevel, index, setFieldValue]);

  // Handle image upload with S3
  const handleImageUpload = async (selected: File) => {
    if (!selected) return;
    // Reset status
    setUploadStatus({ loading: true, error: null });

    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(selected.type)) {
      setUploadStatus({
        loading: false,
        error: UPLOAD_ERRORS.INVALID_FILE_TYPE,
      });
      return;
    }

    // TRIL-635: downscale BEFORE the size check, and record the RESULT's name/type/size on the
    // block below — those fields describe the object that is actually stored. Checking the
    // original first would reject a camera photo we are about to shrink well under the limit,
    // and stamping the original's metadata onto a re-encoded JPEG would simply be false.
    const file = await downscaleImageFile(selected);

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadStatus({ loading: false, error: UPLOAD_ERRORS.FILE_TOO_LARGE });
      return;
    }

    try {
      // Upload the image to S3
      const imageUrl = await uploadImage(file);
      const savedImageKeyInStore = extractS3Key(imageUrl);
      dispatch(SAVED_IMAGE_KEY(savedImageKeyInStore as string));
      // Update the block with the S3 URL
      arrayHelpers.replace(index, {
        ...block,
        data: {
          ...block.data,
          file: {
            url: imageUrl,
            originalName: file.name,
            type: file.type,
            size: file.size,
          },
        },
      });

      setUploadStatus({ loading: false, error: null });
    } catch (error) {
      console.error('Error uploading image:', error);
      setUploadStatus({
        loading: false,
        error:
          error instanceof Error ? error.message : UPLOAD_ERRORS.UPLOAD_FAILED,
      });
    }
  };

  const handleRemoveImage = async (imageKey: string) => {
    if (!imageKey) {
      console.error('No image key provided for deletion');
      return;
    }

    setIsDeleting(true);

    try {
      // Delete from S3
      await deleteImageFromS3(imageKey);
    } catch (error) {
      console.error('Failed to remove image:', error);
      // You might want to show a toast notification here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleReplaceImage = async (imageKey: string) => {
    if (!imageKey) {
      console.error('No image key provided for replacement');
      return;
    }

    setIsDeleting(true);

    try {
      // First, delete the old image from S3
      await deleteImageFromS3(imageKey);
    } catch (error) {
      console.error('Failed to replace image:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUploadOrChange = (event: any) => {
    (fileInputRef as React.MutableRefObject<any>).current?.click();
    return handleReplaceImage(event.target.id);
  };

  const handleDeleteImage = async (event: any) => {
    arrayHelpers.replace(index, {
      ...block,
      data: { ...block.data, file: null },
    });
    return handleRemoveImage(event.target.id);
  };

  switch (block.type) {
    case 'Paragraph':
      return (
        <CaseParagraph
          block={block}
          index={index}
          onChange={(e: { target: { value: any } }) => {
            return setFieldValue(
              `blocks.${index}.data.paragraph`,
              e.target.value,
            );
          }}
          onPaste={(e: React.ClipboardEvent<HTMLTextAreaElement>) => {
            handlePaste(
              e,
              setFieldValue,
              values,
              index,
              isEditingDraft,
              isAddingDraft,
              isEditingPostReplyDraft,
            );
          }}
        />
      );
    case 'Heading':
      return (
        <CaseHeading
          block={block}
          callbackFn={(level) => (
            <button
              key={`H${level}`}
              type="button"
              role="menuitem"
              onClick={() => {
                handleHeadingLevelChange(index, level, setFieldValue);
                handleCloseHeadingMenu(block.id);
              }}
            >
              H{level}
            </button>
          )}
          headingMenuAnchors={headingMenuAnchors}
          index={index}
          values={values}
          onChange={(value: any) => {
            setFieldValue(`blocks.${index}.data.header`, value);
          }}
          onClick={(e) => {
            handleOpenHeadingMenu(e, block.id);
          }}
          onClose={() => {
            handleCloseHeadingMenu(block.id);
          }}
        />
      );
    case 'List': {
      const items = block.data?.items ?? [];
      const hasItems = items.length > 0;
      const listStyle = arrayOfValues[index].data?.style ?? 'unordered';

      const addItem = () => {
        const newItems = [...items, { id: uuidv4(), text: '' }];
        const newData = {
          ...block.data,
          items: newItems,
          style: block.data?.style || 'unordered',
        };
        arrayHelpers.replace(index, { ...block, data: newData });
      };

      return (
        <div className="list-block">
          {!hasItems ? (
            <ButtonComponent size="sm" variant="outline" onClick={addItem}>
              <ListPlus size={16} />
              <span style={{ marginLeft: '0.375rem' }}>Create list</span>
            </ButtonComponent>
          ) : (
            <FieldArray
              name={`blocks.${index}.data.items`}
              render={(itemArrayHelpers) => (
                <div className="list-block__items">
                  {items.map((item: any, itemIndex: number) => (
                    <div key={item.id} className="list-block__row">
                      {itemIndex === items.length - 1 ? (
                        <ButtonComponent
                          iconButton
                          size="sm"
                          variant="ghost"
                          onClick={addItem}
                          aria-label="Add list item"
                        >
                          <ListPlus size={16} />
                        </ButtonComponent>
                      ) : (
                        <span className="list-block__add-spacer" />
                      )}

                      <span className="list-block__marker">
                        {listStyle === 'ordered' ? `${itemIndex + 1}.` : '•'}
                      </span>

                      <input
                        className="list-block__input"
                        name={`blocks.${index}.data.items.${itemIndex}.text`}
                        value={item.text ?? ''}
                        placeholder="Enter item"
                        onChange={(e) => {
                          setFieldValue(
                            `blocks.${index}.data.items.${itemIndex}.text`,
                            e.target.value,
                          );
                        }}
                      />

                      <ButtonComponent
                        iconButton
                        size="sm"
                        variant="ghost"
                        disabled={items.length === 1}
                        onClick={() => itemArrayHelpers.remove(itemIndex)}
                        aria-label="Remove item"
                      >
                        <Trash2 size={14} />
                      </ButtonComponent>

                      {itemIndex === 0 && (
                        <div className="list-block__type-toggle">
                          <ButtonComponent
                            iconButton
                            size="sm"
                            variant={
                              listStyle === 'unordered' ? 'primary' : 'outline'
                            }
                            onClick={() =>
                              setFieldValue(
                                `blocks.${index}.data.style`,
                                'unordered',
                              )
                            }
                            aria-label="Unordered list"
                          >
                            <List size={14} />
                          </ButtonComponent>
                          <ButtonComponent
                            iconButton
                            size="sm"
                            variant={
                              listStyle === 'ordered' ? 'primary' : 'outline'
                            }
                            onClick={() =>
                              setFieldValue(
                                `blocks.${index}.data.style`,
                                'ordered',
                              )
                            }
                            aria-label="Ordered list"
                          >
                            <ListOrdered size={14} />
                          </ButtonComponent>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            />
          )}
        </div>
      );
    }
    case 'Image':
      return (
        <div className="Case__IMAGE w-full">
          {block.data?.file?.url ? (
            <img alt="Uploaded content" src={block.data.file.url} />
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
              <p>No image uploaded</p>
            </div>
          )}

          {uploadStatus.error && (
            <div className="text-red-500 text-sm mt-2">
              {uploadStatus.error}
            </div>
          )}

          <input
            ref={fileInputRef}
            accept="image/*"
            className="hidden"
            type="file"
            onChange={(e) => {
              const file = e.target.files ? e.target.files[0] : null;
              if (file) {
                handleImageUpload(file).then((res) => {
                  console.log('case image input onChange: res', res);
                });
              }
            }}
          />

          <div className="flex space-x-2">
            <ButtonComponent
              id={extractS3Key(block.data?.file?.url as string) ?? undefined}
              variant="outline"
              onClick={(e) => handleUploadOrChange(e)}
              disabled={uploadStatus.loading}
            >
              {uploadStatus.loading
                ? 'Uploading...'
                : block.data?.file?.url
                  ? `${BUTTON_LABELS.CHANGE_IMAGE}`
                  : `${BUTTON_LABELS.UPLOAD_IMAGE}`}
            </ButtonComponent>

            {block.data?.file?.url && (
              <ButtonComponent
                disabled={isDeleting}
                id={extractS3Key(block.data?.file?.url as string) ?? undefined}
                variant="outline"
                color="secondary"
                onClick={(event) => handleDeleteImage(event)}
              >
                {BUTTON_LABELS.REMOVE_IMAGE}
              </ButtonComponent>
            )}
          </div>

          {block.data?.file?.url && (
            <div className="text-xs text-gray-500 mt-1">
              {block.data.file.originalName && (
                <div>Name: {block.data.file.originalName}</div>
              )}
              {block.data.file.size && (
                <div>Size: {Math.round(block.data.file.size / 1024)} KB</div>
              )}
            </div>
          )}
        </div>
      );
    case 'Code':
      return (
        <InputField
          as="textarea"
          className="Type__Code w-full bg-transparent resize-y"
          id={`blocks.${index}.data.code`}
          name={`blocks.${index}.data.code`}
          placeholder="Enter your code here"
          rows={5}
        />
      );
    case 'Quote':
      return (
        <div className="quote-wrapper">
          <div className="border-l-4 border-gray-300 pl-4 italic">
            <InputField
              as="textarea"
              className="w-full p-2 bg-gray-100 rounded resize-none"
              id={`blocks.${index}.data.paragraph`}
              name={`blocks.${index}.data.paragraph`}
              placeholder="Enter a quote"
            />
            <InputField
              className="w-full mt-2 p-1 border-b border-gray-300 text-sm"
              id={`blocks.${index}.data.caption`}
              name={`blocks.${index}.data.caption`}
              placeholder="Caption (optional)"
            />
          </div>
        </div>
      );
    default:
      return (
        <InputField
          className="w-full p-2 border rounded"
          id={`blocks.${index}.data.paragraph`}
          name={`blocks.${index}.data.paragraph`}
          placeholder="Enter text or paste a link"
          type="text"
        />
      );
  }
};
